import { TaJson } from "ta-json";

import { NullableResultPromise } from "../base-types";
import { IExtendedContentHubClient } from "../clients/extended-client";
import { EntityDefinition, IEntityDefinition } from "../contracts/base";
import Guard from "../guard";
import { EntityDefinitionCollectionResource } from "../models/entity-definition-collection-resource";
import { EntityDefinitionResource } from "../models/entity-definition-resource";
import { MemberGroupMapper } from "./member-group-mapper";

export class EntityDefinitionMapper {
    private readonly _client: IExtendedContentHubClient;
    private readonly _memberGroupMapper: MemberGroupMapper;

    constructor(client: IExtendedContentHubClient) {
        Guard.notNullOrUndefined(client, "client");

        this._client = client;
        this._memberGroupMapper = new MemberGroupMapper(client);
    }

    //#region To SDK
    async mapEntityDefinitionsAsync(
        resources: Array<EntityDefinitionResource> | EntityDefinitionCollectionResource
    ): Promise<Array<IEntityDefinition>> {
        if (!resources) return [];

        if (!(resources instanceof Array)) {
            resources = resources.items;
        }

        if (resources.length === 0) return [];

        const list: Array<IEntityDefinition> = [];

        for (const resource of resources) {
            const entityDefinition = await this.mapEntityDefinitionAsync(resource);
            if (entityDefinition != null) {
                list.push(entityDefinition);
            }
        }

        return list;
    }

    async mapEntityDefinitionAsync(resource: EntityDefinitionResource): NullableResultPromise<IEntityDefinition> {
        if (!resource) return null;

        const {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            entities,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            relatedPaths,
            createdBy,
            modifiedBy,
            permissions,
            memberGroups,
            labels,
            ...definitionData
        } = TaJson.deserialize<EntityDefinitionResource>(TaJson.serialize(resource), EntityDefinitionResource);

        const definition = new EntityDefinition({
            ...definitionData,
            createdBy: (await this._client.linkHelper.idFromEntityAsync(createdBy)) || undefined,
            modifiedBy: (await this._client.linkHelper.idFromEntityAsync(modifiedBy)) || undefined,
            memberGroups: await this._memberGroupMapper.mapMemberGroupsAsync(memberGroups),
            labels: JSON.parse(JSON.stringify(labels)),
            permissions,
        });

        return definition;
    }
    //#endregion

    //#region To API resource
    async mapEntityDefinitionResourceAsync(
        definition: IEntityDefinition
    ): NullableResultPromise<EntityDefinitionResource> {
        if (definition == null) return null;

        const memberGroups = await this._memberGroupMapper.mapMemberGroupResourcesAsync(definition.memberGroups);

        return new EntityDefinitionResource({
            name: definition.name,
            id: definition.id || 0,
            displayTemplate: definition.displayTemplate,
            isManualSortingAllowed: definition.isManualSortingAllowed,
            isSystemOwned: definition.isSystemOwned,
            isLight: definition.isLight,
            isTaxonomyItemDefinition: definition.isTaxonomyItemDefinition,
            isPathEnabledDefinition: definition.isPathEnabledDefinition,
            memberGroups: memberGroups,
            labels: JSON.parse(JSON.stringify(definition.labels)),
            usageType: definition.usageType,
        });
    }
    //#endregion
}
